/**
 * Garden Object Types Configuration
 *
 * Comprehensive definitions for all 16 garden object types with:
 * - Basic metadata (id, name, icon, category)
 * - Allowed drawing tools
 * - Behavior flags (isPlantable, castsShadow)
 * - Default styles
 * - MVP and future properties with complete schemas
 */

import { GARDEN_OBJECT_CATEGORIES } from './gardenObjectCategories';

// Tool constants for allowed tools
export const DRAWING_TOOLS = {
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
  POLYGON: 'polygon',
  BEZIER: 'bezier',
  LINE: 'line',
  POINT: 'point', // Click placement (tor: snap onto zaun/mauer/gebaeude/gewaechshaus, #611)
  CENTERLINE: 'centerline', // Offene Mittellinie + Breite (weg, #720) — eckig/kurvig via data.verlauf
};

// Property field types
export const PROPERTY_TYPES = {
  TEXT: 'text',
  NUMBER: 'number',
  SELECT: 'select',
  BOOLEAN: 'boolean',
  COLOR: 'color',
  SLIDER: 'slider',
};

// ============================================================================
// GARDEN OBJECT TYPE DEFINITIONS
// ============================================================================

export const GARDEN_OBJECT_TYPES_CONFIG = {
  // ========================================================================
  // STRUKTUREN (Structures)
  // ========================================================================

  WEG: {
    id: 'weg',
    name: 'Weg',
    icon: 'wegIcon',  // Custom PNG icon
    category: GARDEN_OBJECT_CATEGORIES.STRUKTUREN,
    // #720: Weg wird als Mittellinie + Breite gezeichnet (CENTERLINE), nicht mehr
    // als geschlossene Fläche. Bestandswege (Bezier/Polygon) bleiben als Legacy.
    allowedTools: [DRAWING_TOOLS.CENTERLINE],
    isPlantable: false,
    castsShadow: false,
    defaultStyle: {
      fill: '#C4A57B',
      stroke: '#A08060',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: 'z.B. Gartenweg Süd',
          defaultValue: '',
          order: 1,
        },
        // #720: Wegbreite als Bandbreite (wie rasenkante.width); wirkt als
        // Stroke-Breite in 2D und als Bandbreite in 3D.
        width: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Breite',
          unit: 'cm',
          min: 20,
          max: 400,
          step: 5,
          defaultValue: 100,
          order: 2,
        },
        // #720: Verlauf der Mittellinie — kurvig (geglättet) oder eckig (Polylinie).
        verlauf: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Verlauf',
          options: [
            { value: 'kurvig', label: 'Kurvig' },
            { value: 'eckig', label: 'Eckig' },
          ],
          defaultValue: 'kurvig',
          order: 3,
        },
        material: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Material',
          options: [
            { value: 'kies', label: 'Kies' },
            { value: 'pflaster', label: 'Pflaster' },
            { value: 'holz', label: 'Holz' },
            { value: 'rindenmulch', label: 'Rindenmulch' },
            { value: 'naturstein', label: 'Naturstein' },
          ],
          defaultValue: 'pflaster',
          order: 4,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#C4A57B',
          order: 5,
        },
      },
      future: {
        beleuchtung: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Beleuchtung',
          defaultValue: false,
        },
        barrierefreiheit: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Barrierefrei',
          defaultValue: false,
        },
      },
    },
  },

  MAUER: {
    id: 'mauer',
    name: 'Mauer',
    icon: 'Square',
    category: GARDEN_OBJECT_CATEGORIES.STRUKTUREN,
    allowedTools: [DRAWING_TOOLS.RECTANGLE, DRAWING_TOOLS.POLYGON, DRAWING_TOOLS.LINE],
    isPlantable: false,
    castsShadow: true,
    defaultStyle: {
      fill: '#78716C',
      stroke: '#57534E',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        height: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Höhe',
          unit: 'cm',
          min: 20,
          max: 300,
          step: 5,
          defaultValue: 100,
          order: 2,
        },
        material: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Material',
          options: [
            { value: 'brick', label: 'Ziegel' },
            { value: 'stone', label: 'Naturstein' },
            { value: 'concrete', label: 'Beton' },
            { value: 'gabion', label: 'Gabionen' },
          ],
          defaultValue: 'stone',
          order: 3,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#78716C',
          order: 4,
        },
      },
      future: {
        begruenung: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Begrünung möglich',
          defaultValue: false,
        },
        beschattung_wirft: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Wirft Schatten',
          defaultValue: true,
        },
      },
    },
  },

  ZAUN: {
    id: 'zaun',
    name: 'Zaun',
    icon: 'Fence',
    category: GARDEN_OBJECT_CATEGORIES.STRUKTUREN,
    allowedTools: [DRAWING_TOOLS.LINE],
    isPlantable: false,
    castsShadow: true,
    defaultStyle: {
      fill: 'transparent',
      stroke: '#8B4513',
      strokeWidth: 8,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        height: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Höhe',
          unit: 'cm',
          min: 50,
          max: 250,
          step: 5,
          defaultValue: 180,
          order: 2,
        },
        material: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Material',
          options: [
            { value: 'holz', label: 'Holz' },
            { value: 'metall', label: 'Metall' },
            { value: 'kunststoff', label: 'Kunststoff' },
            { value: 'wpc', label: 'WPC' },
            { value: 'draht', label: 'Draht' },
          ],
          defaultValue: 'holz',
          order: 3,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#8B4513',
          order: 4,
        },
        zaun_typ: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Zaun-Typ',
          options: [
            { value: 'sichtschutz', label: 'Sichtschutz' },
            { value: 'jägerzaun', label: 'Jägerzaun' },
            { value: 'lattenzaun', label: 'Lattenzaun' },
            { value: 'staketen', label: 'Staketen' },
          ],
          defaultValue: 'lattenzaun',
          order: 5,
        },
      },
      future: {
        rankgitter: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Rankgitter',
          defaultValue: false,
        },
      },
    },
  },

  TOR: {
    id: 'tor',
    name: 'Tür / Tor',
    icon: 'DoorOpen',
    category: GARDEN_OBJECT_CATEGORIES.STRUKTUREN,
    allowedTools: [DRAWING_TOOLS.POINT],
    isPlantable: false,
    castsShadow: true,
    defaultStyle: {
      fill: '#2F4F4F',
      stroke: '#1a3333',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        width: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Breite',
          unit: 'cm',
          min: 60,
          max: 500,
          step: 5,
          defaultValue: 100,
          order: 2,
        },
        height: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Höhe',
          unit: 'cm',
          min: 80,
          max: 250,
          step: 5,
          defaultValue: 120,
          order: 3,
        },
        material: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Material',
          options: [
            { value: 'holz', label: 'Holz' },
            { value: 'metall', label: 'Metall' },
            { value: 'schmiedeeisen', label: 'Schmiedeeisen' },
            { value: 'kunststoff', label: 'Kunststoff' },
            { value: 'aluminium', label: 'Aluminium' },
          ],
          defaultValue: 'holz',
          order: 4,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#2F4F4F',
          order: 5,
        },
        // hinge (Aufhängung/Anschlag) + swing (Öffnungsrichtung) drive the
        // floor-plan door symbol (#611). Edited via the dedicated GateProperties
        // panel; `oeffnungsrichtung` (legacy) is mapped to `hinge` on old gates.
        hinge: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Aufhängung',
          options: [
            { value: 'left', label: 'Links' },
            { value: 'right', label: 'Rechts' },
            { value: 'double', label: 'Doppelflügel' },
          ],
          defaultValue: 'left',
          order: 6,
        },
        swing: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Öffnungsrichtung',
          options: [
            { value: 'in', label: 'Innen' },
            { value: 'out', label: 'Außen' },
          ],
          defaultValue: 'in',
          order: 7,
        },
      },
      future: {
        // Schiebetor/Doppeltor as distinct symbols — v1 only supports drehtuer (#611 follow-up)
        tor_typ: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Tor-Typ',
          options: [
            { value: 'drehtuer', label: 'Drehtür' },
            { value: 'schiebetor', label: 'Schiebetor' },
            { value: 'doppeltor', label: 'Doppeltor' },
          ],
          defaultValue: 'drehtuer',
        },
      },
    },
  },

  // Fenster (#690): zweites Öffnungs-Element, teilt die Link-/Snap-Mechanik des
  // Tors (#611), sitzt aber auf einer Brüstungshöhe (sillHeight) und rastet nur
  // an gebaeude/gewaechshaus ein (Registry in gateLinkUtils, nicht hier). In 3D
  // ein transparentes Glas-Paneel, im Grundriss ein schmaler Doppelstrich ohne
  // Anschlag/Schwenkbogen — daher kein hinge/swing. Eigenschaften über das
  // gemeinsame GateProperties-Panel (Fenster-Variante).
  FENSTER: {
    id: 'fenster',
    name: 'Fenster',
    icon: 'AppWindow',
    category: GARDEN_OBJECT_CATEGORIES.STRUKTUREN,
    allowedTools: [DRAWING_TOOLS.POINT],
    isPlantable: false,
    castsShadow: false,
    defaultStyle: {
      fill: '#CFE6EF',
      stroke: '#5B7C8A',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        width: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Breite',
          unit: 'cm',
          min: 40,
          max: 400,
          step: 5,
          defaultValue: 100,
          order: 2,
        },
        height: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Höhe',
          unit: 'cm',
          min: 40,
          max: 250,
          step: 5,
          defaultValue: 120,
          order: 3,
        },
        // Brüstungshöhe: Unterkante des Fensters über dem Boden. Der 3D-Adapter
        // klemmt sillHeight + height auf die Wandhöhe des Elternobjekts (#690).
        sillHeight: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Brüstungshöhe',
          unit: 'cm',
          min: 0,
          max: 200,
          step: 5,
          defaultValue: 90,
          order: 4,
        },
        // Kein material-Property: das 3D-Fenster ist immer Glas (window-panel).
        // Eine Rahmen-Materialauswahl ohne 3D-Wirkung wäre eine wirkungslose
        // Property (#701) — bewusst weggelassen.
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Rahmenfarbe',
          defaultValue: '#5B7C8A',
          order: 5,
        },
      },
    },
  },

  RASENKANTE: {
    id: 'rasenkante',
    name: 'Rasenkante',
    icon: 'Ruler',
    category: GARDEN_OBJECT_CATEGORIES.STRUKTUREN,
    allowedTools: [DRAWING_TOOLS.LINE],
    isPlantable: false,
    castsShadow: false,
    defaultStyle: {
      fill: 'transparent',
      stroke: '#4B5563',
      strokeWidth: 4,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        width: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Breite',
          unit: 'cm',
          min: 3,
          max: 15,
          step: 1,
          defaultValue: 8,
          order: 2,
        },
        material: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Material',
          options: [
            { value: 'metall', label: 'Metall' },
            { value: 'kunststoff', label: 'Kunststoff' },
            { value: 'stein', label: 'Stein' },
            { value: 'beton', label: 'Beton' },
            { value: 'holz', label: 'Holz' },
          ],
          defaultValue: 'metall',
          order: 3,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#4B5563',
          order: 4,
        },
      },
      future: {
        form: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Form',
          options: [
            { value: 'gerade', label: 'Gerade' },
            { value: 'gewellt', label: 'Gewellt' },
          ],
          defaultValue: 'gerade',
        },
      },
    },
  },

  // ========================================================================
  // GEBÄUDE (Buildings)
  // ========================================================================

  GEBAEUDE: {
    id: 'gebaeude',
    name: 'Gebäude',
    icon: 'Home',
    category: GARDEN_OBJECT_CATEGORIES.GEBAEUDE,
    allowedTools: [DRAWING_TOOLS.RECTANGLE, DRAWING_TOOLS.CIRCLE, DRAWING_TOOLS.POLYGON],
    isPlantable: false,
    castsShadow: true,
    defaultStyle: {
      fill: '#E5E7EB',
      stroke: '#6B7280',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        type: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Typ',
          options: [
            { value: 'gartenhaus', label: 'Gartenhaus' },
            { value: 'garage', label: 'Garage' },
            { value: 'carport', label: 'Carport' },
            { value: 'geraeteschuppen', label: 'Geräteschuppen' },
            { value: 'werkstatt', label: 'Werkstatt' },
            { value: 'poolhaus', label: 'Poolhaus' },
            { value: 'sauna', label: 'Sauna' },
            { value: 'pavillon', label: 'Pavillon' },
            { value: 'wohnhaus', label: 'Wohnhaus (Hauptgebäude)' },
          ],
          defaultValue: 'gartenhaus',
          order: 2,
        },
        buildingHeight: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Gebäudehöhe',
          unit: 'cm',
          min: 200,
          max: 1500,
          step: 10,
          defaultValue: 250,
          order: 3,
        },
        wallColor: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Wandfarbe',
          defaultValue: '#8B4513',
          order: 4,
        },
        // Dachfarbe (#722): eigener Farbkanal fürs Dach, unabhängig von der Wand.
        // Nur 3D (wie dachform) — die 2D-Aufsicht füllt das Gebäude weiter mit der
        // Wandfarbe. Default ist ein ruhiger Anthrazit-Dachton, damit Dächer von
        // Haus aus als Dach lesen und nicht die Wandfarbe erben (kein useCustomColor-
        // Gate: buildSceneModel wendet roofColor immer an, mit Config-Default als
        // Fallback für Alt-Objekte ohne das Feld).
        roofColor: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Dachfarbe (3D)',
          defaultValue: '#4A4E54',
          order: 5,
        },
        // Dachform (#689): wirkt in der 3D-Ansicht. Walmdach ist bewusst NICHT
        // dabei — es bräuchte eigene Geometrie und würde sonst auf eine der drei
        // gerenderten Formen zurückfallen. Ist der Wert nicht gesetzt, leitet der
        // 3D-Adapter (und die Panel-Vorbelegung) die Form aus dem Gebäude-Typ ab
        // (getBuildingRoofForm), damit Bestandsgebäude nicht auf einen pauschalen
        // Default springen.
        dachform: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Dachform (3D)',
          options: [
            { value: 'satteldach', label: 'Satteldach' },
            { value: 'flachdach', label: 'Flachdach' },
            { value: 'pultdach', label: 'Pultdach' },
          ],
          defaultValue: 'satteldach',
          order: 6,
        },
      },
      future: {
        beschattung_wirft: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Wirft Schatten',
          defaultValue: true,
        },
      },
    },
  },

  GEWAECHSHAUS: {
    id: 'gewaechshaus',
    name: 'Gewächshaus',
    icon: 'gardenhouseIcon',  // Custom PNG icon
    category: GARDEN_OBJECT_CATEGORIES.GEBAEUDE,
    allowedTools: [DRAWING_TOOLS.RECTANGLE, DRAWING_TOOLS.POLYGON],
    isPlantable: false,
    castsShadow: true,
    defaultStyle: {
      fill: '#F0F8FF',
      stroke: '#87CEEB',
      strokeWidth: 2,
      opacity: 0.7,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        type: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Typ',
          options: [
            { value: 'anlehngew', label: 'Anlehngewächshaus' },
            { value: 'freistehend', label: 'freistehendes Gewächshaus' },
            { value: 'folientunnel', label: 'Foliengewächshaus / Tunnel' },
            { value: 'tomatenhaus', label: 'Tomatenhaus (klein)' },
            { value: 'wintergarten', label: 'Wintergarten (fest)' },
            { value: 'fruehbeet', label: 'Frühbeetkasten' },
          ],
          defaultValue: 'freistehend',
          order: 2,
        },
        ridgeHeight: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Firsthöhe',
          unit: 'cm',
          min: 50,
          max: 400,
          step: 10,
          defaultValue: 200,
          order: 3,
        },
        material: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Material',
          options: [
            { value: 'glas', label: 'Glas' },
            { value: 'polycarbonat', label: 'Polycarbonat' },
            { value: 'acrylglas', label: 'Acrylglas' },
            { value: 'folie', label: 'Folie' },
          ],
          defaultValue: 'glas',
          order: 4,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#F0F8FF',
          order: 5,
        },
      },
      future: {
        beheizt: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Beheizt',
          defaultValue: false,
        },
        bewaesserung: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Automatische Bewässerung',
          defaultValue: false,
        },
      },
    },
  },

  // ========================================================================
  // PFLANZFLÄCHEN (Planting Areas)
  // ========================================================================

  BEET: {
    id: 'beet',
    name: 'Beet',
    icon: 'Flower2',
    category: GARDEN_OBJECT_CATEGORIES.PFLANZFLAECHEN,
    allowedTools: [DRAWING_TOOLS.RECTANGLE, DRAWING_TOOLS.CIRCLE, DRAWING_TOOLS.POLYGON, DRAWING_TOOLS.BEZIER],
    isPlantable: true,
    castsShadow: false,
    defaultStyle: {
      fill: '#8B5A2B',
      stroke: '#5D3A1A',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        // Bodenart (#700): Optionswerte auf das Wizard-/Katalog-Vokabular gebracht
        // (WIZARD_TO_CATALOG_SOIL_MAP), damit „Pflanzen für dieses Beet" den
        // Katalog nach der Beet-Bodenart vorfiltern kann. 'inherit' übernimmt die
        // gartenweite Wizard-Einstellung.
        soilType: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Bodenart',
          options: [
            { value: 'inherit', label: 'Vom Garten übernehmen' },
            { value: 'sandy', label: 'Sand' },
            { value: 'loamy', label: 'Lehm' },
            { value: 'clay', label: 'Ton' },
            { value: 'loess', label: 'Löss' },
            { value: 'humus', label: 'Humus' },
            { value: 'mixed', label: 'Mischboden' },
          ],
          defaultValue: 'inherit',
          order: 2,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#8B5A2B',
          order: 3,
        },
      },
      future: {
        pflanzplan: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Pflanzplan',
          defaultValue: '',
        },
        bewaesserung: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Automatische Bewässerung',
          defaultValue: false,
        },
      },
    },
  },

  HOCHBEET: {
    id: 'hochbeet',
    name: 'Hochbeet',
    icon: 'hochbeetIcon',  // Custom PNG icon
    category: GARDEN_OBJECT_CATEGORIES.PFLANZFLAECHEN,
    allowedTools: [DRAWING_TOOLS.RECTANGLE, DRAWING_TOOLS.CIRCLE, DRAWING_TOOLS.POLYGON],
    isPlantable: true,
    castsShadow: true,
    defaultStyle: {
      fill: '#8B4513',
      stroke: '#5D3A1A',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        height: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Höhe',
          unit: 'cm',
          min: 30,
          max: 120,
          step: 5,
          defaultValue: 80,
          order: 2,
        },
        material: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Material',
          options: [
            { value: 'holz', label: 'Holz' },
            { value: 'metall', label: 'Metall' },
            { value: 'kunststoff', label: 'Kunststoff' },
            { value: 'stein', label: 'Stein' },
            { value: 'beton', label: 'Beton' },
            { value: 'paletten', label: 'Paletten' },
          ],
          defaultValue: 'holz',
          order: 3,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#8B4513',
          order: 4,
        },
      },
      future: {
        schichtaufbau: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Schichtaufbau angelegt',
          defaultValue: false,
        },
        wuehlmausschutz: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Wühlmausschutz',
          defaultValue: false,
        },
      },
    },
  },

  RASEN: {
    id: 'rasen',
    name: 'Rasen',
    icon: 'Sprout',
    category: GARDEN_OBJECT_CATEGORIES.PFLANZFLAECHEN,
    allowedTools: [DRAWING_TOOLS.RECTANGLE, DRAWING_TOOLS.CIRCLE, DRAWING_TOOLS.POLYGON, DRAWING_TOOLS.BEZIER],
    isPlantable: false,
    castsShadow: false,
    defaultStyle: {
      fill: '#22C55E',
      stroke: '#16A34A',
      strokeWidth: 1,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        type: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Rasensorte',
          options: [],                    // Dynamically populated from plant DB
          dynamicOptions: 'lawnTypes',   // Marker for DynamicGardenObjectProperties
          defaultValue: '',
          placeholder: 'Rasensorte wählen...',
          order: 2,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#22C55E',
          order: 3,
        },
      },
      future: {
        maehroboter: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Mähroboter',
          defaultValue: false,
        },
        bewaesserung: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Automatische Bewässerung',
          defaultValue: false,
        },
      },
    },
  },

  BLUMENKASTEN: {
    id: 'blumenkasten',
    name: 'Blumenkasten',
    icon: 'Square',
    category: GARDEN_OBJECT_CATEGORIES.PFLANZFLAECHEN,
    allowedTools: [DRAWING_TOOLS.RECTANGLE],
    isPlantable: true,
    castsShadow: false,
    defaultStyle: {
      fill: '#A16207',
      stroke: '#854D0E',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: 'z.B. Balkonkasten',
          defaultValue: '',
          order: 1,
        },
        boxWidth: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Länge',
          unit: 'cm',
          min: 20,
          max: 150,
          step: 5,
          defaultValue: 60,
          order: 2,
        },
        boxHeight: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Breite',
          unit: 'cm',
          min: 10,
          max: 50,
          step: 5,
          defaultValue: 20,
          order: 3,
        },
        boxDepth: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Tiefe',
          unit: 'cm',
          min: 10,
          max: 40,
          step: 5,
          defaultValue: 20,
          order: 4,
        },
        material: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Material',
          options: [
            { value: 'terracotta', label: 'Terracotta' },
            { value: 'kunststoff', label: 'Kunststoff' },
            { value: 'holz', label: 'Holz' },
            { value: 'metall', label: 'Metall' },
          ],
          defaultValue: 'terracotta',
          order: 5,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#A16207',
          order: 6,
        },
        mountHeight: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Montagehöhe',
          unit: 'cm',
          min: 0,
          max: 200,
          step: 10,
          defaultValue: 100,
          order: 7,
        },
      },
      future: {
        bewaesserungssystem: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Bewässerungssystem',
          defaultValue: false,
        },
        standort: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Standort',
          options: [
            { value: 'balkon', label: 'Balkon' },
            { value: 'terrasse', label: 'Terrasse' },
            { value: 'fenster', label: 'Fensterbank' },
          ],
          defaultValue: 'balkon',
        },
      },
    },
  },

  BLUMENAMPEL: {
    id: 'blumenampel',
    name: 'Blumenampel',
    icon: 'Circle',
    category: GARDEN_OBJECT_CATEGORIES.PFLANZFLAECHEN,
    allowedTools: [DRAWING_TOOLS.CIRCLE],
    isPlantable: true,
    castsShadow: false,
    defaultStyle: {
      fill: '#A16207',
      stroke: '#854D0E',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        diameter: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Durchmesser',
          unit: 'cm',
          min: 15,
          max: 60,
          step: 5,
          defaultValue: 30,
          order: 2,
        },
        material: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Material',
          options: [
            { value: 'kunststoff', label: 'Kunststoff' },
            { value: 'kokos', label: 'Kokosfaser' },
            { value: 'metall', label: 'Metall' },
            { value: 'rattan', label: 'Rattan' },
          ],
          defaultValue: 'kunststoff',
          order: 3,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#A16207',
          order: 4,
        },
        mountHeight: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Aufhängehöhe',
          unit: 'cm',
          min: 150,
          max: 300,
          step: 10,
          defaultValue: 200,
          order: 5,
        },
      },
      future: {
        befestigung: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Befestigung',
          options: [
            { value: 'haken', label: 'Haken' },
            { value: 'stange', label: 'Stange' },
            { value: 'halterung', label: 'Wandhalterung' },
          ],
          defaultValue: 'haken',
        },
      },
    },
  },

  TOPF: {
    id: 'topf',
    name: 'Topf',
    icon: 'Circle',
    category: GARDEN_OBJECT_CATEGORIES.PFLANZFLAECHEN,
    allowedTools: [DRAWING_TOOLS.RECTANGLE, DRAWING_TOOLS.CIRCLE],
    isPlantable: true,
    castsShadow: false,
    defaultStyle: {
      fill: '#A16207',
      stroke: '#854D0E',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        potHeight: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Höhe',
          unit: 'cm',
          min: 10,
          max: 80,
          step: 5,
          defaultValue: 30,
          order: 2,
        },
        material: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Material',
          options: [
            { value: 'terracotta', label: 'Terracotta' },
            { value: 'kunststoff', label: 'Kunststoff' },
            { value: 'keramik', label: 'Keramik' },
            { value: 'metall', label: 'Metall' },
            { value: 'stein', label: 'Stein' },
          ],
          defaultValue: 'terracotta',
          order: 4,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#A16207',
          order: 5,
        },
      },
      future: {
        untersetzer: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Untersetzer',
          defaultValue: false,
        },
        frostfest: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Frostfest',
          defaultValue: false,
        },
      },
    },
  },

  // ========================================================================
  // WASSER (Water)
  // ========================================================================

  TEICH: {
    id: 'teich',
    name: 'Teich',
    icon: 'Droplets',
    category: GARDEN_OBJECT_CATEGORIES.WASSER,
    allowedTools: [DRAWING_TOOLS.RECTANGLE, DRAWING_TOOLS.CIRCLE, DRAWING_TOOLS.POLYGON, DRAWING_TOOLS.BEZIER],
    isPlantable: true,
    castsShadow: false,
    defaultStyle: {
      fill: '#0EA5E9',
      stroke: '#0284C7',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        depth: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Tiefe',
          unit: 'cm',
          min: 20,
          max: 200,
          step: 10,
          defaultValue: 80,
          order: 2,
        },
        type: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Typ',
          options: [
            { value: 'gartenteich', label: 'Gartenteich (Zierteich)' },
            { value: 'schwimmteich', label: 'Schwimmteich' },
            { value: 'fischteich', label: 'Fischteich' },
            { value: 'pool', label: 'Pool' },
            { value: 'bachlauf', label: 'Bachlauf' },
            { value: 'quellstein', label: 'Quellstein' },
          ],
          defaultValue: 'gartenteich',
          order: 3,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#0EA5E9',
          order: 4,
        },
      },
      future: {
        filter: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Filteranlage',
          defaultValue: false,
        },
        pumpe: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Pumpe',
          defaultValue: false,
        },
        fische: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Fische',
          defaultValue: false,
        },
      },
    },
  },

  // ========================================================================
  // SONSTIGES (Miscellaneous)
  // ========================================================================

  KOMPOST: {
    id: 'kompost',
    name: 'Kompost',
    icon: 'Recycle',
    category: GARDEN_OBJECT_CATEGORIES.SONSTIGES,
    allowedTools: [DRAWING_TOOLS.RECTANGLE, DRAWING_TOOLS.CIRCLE],
    isPlantable: false,
    castsShadow: false,
    defaultStyle: {
      fill: '#713F12',
      stroke: '#422006',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        type: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Typ',
          options: [
            { value: 'thermokomposter', label: 'Thermokomposter' },
            { value: 'offener', label: 'offener Komposter (Holzlattung)' },
            { value: 'komposthaufen', label: 'Komposthaufen' },
            { value: 'drahtkomposter', label: 'Drahtkomposter' },
            { value: 'schnellkomposter', label: 'Schnellkomposter' },
          ],
          defaultValue: 'thermokomposter',
          order: 2,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#C4A57B',
          order: 3,
        },
      },
      future: {
        volumen: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Volumen (Liter)',
          unit: 'L',
          min: 100,
          max: 1000,
          step: 50,
          defaultValue: 400,
        },
      },
    },
  },

  TERRASSE: {
    id: 'terrasse',
    name: 'Terrasse',
    icon: 'LayoutGrid',
    category: GARDEN_OBJECT_CATEGORIES.SONSTIGES,
    allowedTools: [DRAWING_TOOLS.RECTANGLE, DRAWING_TOOLS.POLYGON, DRAWING_TOOLS.BEZIER],
    isPlantable: false,
    castsShadow: false,
    defaultStyle: {
      fill: '#D6D3D1',
      stroke: '#A8A29E',
      strokeWidth: 2,
      opacity: 1,
    },
    properties: {
      mvp: {
        name: {
          type: PROPERTY_TYPES.TEXT,
          label: 'Name',
          placeholder: '',
          defaultValue: '',
          order: 1,
        },
        material: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Material',
          options: [
            { value: 'holz', label: 'Holz' },
            { value: 'wpc', label: 'WPC' },
            { value: 'pflaster', label: 'Pflaster' },
            { value: 'naturstein', label: 'Naturstein' },
            { value: 'beton', label: 'Beton' },
            { value: 'fliesen', label: 'Fliesen / Keramik' },
            { value: 'kies', label: 'Kies' },
          ],
          defaultValue: 'holz',
          order: 2,
        },
        color: {
          type: PROPERTY_TYPES.COLOR,
          label: 'Farbe',
          defaultValue: '#8B4513',
          order: 3,
        },
        // Überdachung (#693): ersetzt die frühere folgenlose Checkbox `ueberdacht`.
        // Die Auswahl erzeugt in 3D ein Bauwerk (Stützen + Dachplatte / Markise /
        // Wintergarten) und in 2D ein Grundriss-Symbol; sie wirft Schatten (#291).
        // Migration alter Gärten: ueberdacht:true → 'terrassendach' (applyCanvasObjects).
        ueberdachung: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Überdachung',
          options: [
            { value: 'keine', label: 'Keine' },
            { value: 'terrassendach', label: 'Terrassendach' },
            { value: 'markise', label: 'Markise' },
            { value: 'wintergarten', label: 'Wintergarten' },
          ],
          defaultValue: 'keine',
          order: 4,
        },
        // Dachform der Überdachung (3D). Nutzt dasselbe Vokabular wie das
        // Gebäudedach (#689). Sichtbar für Terrassendach UND Wintergarten (beide
        // haben ein formbares Dach); die Markise trägt immer eine Pult-Kappe.
        dachform: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Dachform (3D)',
          options: [
            { value: 'pultdach', label: 'Pultdach' },
            { value: 'satteldach', label: 'Satteldach' },
            { value: 'flachdach', label: 'Flachdach' },
          ],
          defaultValue: 'pultdach',
          order: 5,
        },
        // Traufhöhe der Überdachung (Unterkante Dach über der Terrasse).
        dachHoehe: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Traufhöhe',
          unit: 'cm',
          min: 200,
          max: 400,
          step: 10,
          defaultValue: 260,
          order: 6,
        },
        dachMaterial: {
          type: PROPERTY_TYPES.SELECT,
          label: 'Dachmaterial',
          options: [
            { value: 'glas', label: 'Glas' },
            { value: 'polycarbonat', label: 'Polycarbonat' },
            { value: 'holz', label: 'Holz' },
          ],
          defaultValue: 'glas',
          order: 7,
        },
        // Markisen-Felder (#693 Phase 2). Nur sichtbar, wenn ueberdachung ===
        // 'markise'. Die Ankerkante (Aufhängekante) wird in 3D aus der Terrassen-
        // Form ABGELEITET (längste Kante der Bounding-Box) — kein eigenes Feld.
        // Ausfall = Auskragung der Markise von der Ankerkante über die Terrasse.
        markiseAusfall: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Ausfall (Auskragung)',
          unit: 'cm',
          min: 100,
          max: 400,
          step: 10,
          defaultValue: 250,
          order: 8,
        },
        // Anbauhöhe: auf welcher Höhe die Markise an der Ankerkante hängt.
        markiseHoehe: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Anbauhöhe',
          unit: 'cm',
          min: 200,
          max: 350,
          step: 10,
          defaultValue: 260,
          order: 9,
        },
        // Ein-/ausgefahren: eingefahren erzeugt keine Geometrie und keinen
        // Schatten (die Markise ist eingerollt).
        markiseAusgefahren: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Ausgefahren',
          defaultValue: true,
          order: 10,
        },
        // Wintergarten-Feld (#693 Phase 3). Nur sichtbar, wenn ueberdachung ===
        // 'wintergarten'. Der Wintergarten ist ein raumbildendes, verglastes
        // Bauwerk (gemeinsame Geometrie mit dem Gewächshaus): Wände bis zur
        // Wandhöhe (Traufe) plus Dach. Dachform und Dachmaterial teilt es mit dem
        // Terrassendach; die Firsthöhe wird aus Wandhöhe + Grundriss abgeleitet.
        wintergartenWandhoehe: {
          type: PROPERTY_TYPES.NUMBER,
          label: 'Wandhöhe',
          unit: 'cm',
          min: 200,
          max: 350,
          step: 10,
          defaultValue: 250,
          order: 11,
        },
      },
      future: {
        beleuchtung: {
          type: PROPERTY_TYPES.BOOLEAN,
          label: 'Beleuchtung',
          defaultValue: false,
        },
      },
    },
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all object type IDs
 */
export const getAllObjectTypeIds = () => {
  return Object.keys(GARDEN_OBJECT_TYPES_CONFIG);
};

/**
 * Get object type configuration by ID
 * Searches by the object's `id` property, not the config key
 */
export const getObjectTypeById = (typeId) => {
  // Find object by matching the id property (not the config key)
  const objectType = Object.values(GARDEN_OBJECT_TYPES_CONFIG).find(
    (type) => type.id === typeId
  );
  return objectType || null;
};

/**
 * Get all object types for a specific category
 */
export const getObjectTypesByCategory = (categoryId) => {
  return Object.values(GARDEN_OBJECT_TYPES_CONFIG).filter(
    (type) => type.category === categoryId
  );
};

/**
 * Get all plantable object types
 */
export const getPlantableObjectTypes = () => {
  return Object.values(GARDEN_OBJECT_TYPES_CONFIG).filter(
    (type) => type.isPlantable
  );
};

/**
 * Get all shadow-casting object types
 */
export const getShadowCastingObjectTypes = () => {
  return Object.values(GARDEN_OBJECT_TYPES_CONFIG).filter(
    (type) => type.castsShadow
  );
};

/**
 * Get allowed tools for an object type
 */
export const getAllowedTools = (typeId) => {
  const objectType = getObjectTypeById(typeId);
  return objectType ? objectType.allowedTools : [];
};

/**
 * Check if a tool is allowed for an object type
 */
export const isToolAllowed = (typeId, toolId) => {
  const allowedTools = getAllowedTools(typeId);
  return allowedTools.includes(toolId);
};

/**
 * Get MVP properties for an object type
 */
export const getMvpProperties = (typeId) => {
  const objectType = getObjectTypeById(typeId);
  return objectType?.properties?.mvp || {};
};

/**
 * Get future properties for an object type
 */
export const getFutureProperties = (typeId) => {
  const objectType = getObjectTypeById(typeId);
  return objectType?.properties?.future || {};
};

/**
 * Get all properties (MVP + future) for an object type
 */
export const getAllProperties = (typeId) => {
  const mvp = getMvpProperties(typeId);
  const future = getFutureProperties(typeId);
  return { ...mvp, ...future };
};

/**
 * Get the mvp COLOR property key for an object type.
 *
 * Most types name their color property `color`; Gebäude names it `wallColor`.
 * Consumers (3D-Adapter, Textur-Sync) leiten den Schlüssel hierüber ab, statt
 * `data.color` fest anzunehmen — so erreicht auch `wallColor` seine Verbraucher
 * ohne Umbenennung oder Migration bestehender Gärten (#691).
 *
 * @param {string} typeId - Object type ID (e.g., 'gebaeude', 'weg')
 * @returns {string|null} The color property key, or null if the type has none.
 */
export const getColorPropertyKey = (typeId) => {
  const objectType = getObjectTypeById(typeId);
  const mvp = objectType?.properties?.mvp || {};
  const key = Object.keys(mvp).find((k) => mvp[k].type === PROPERTY_TYPES.COLOR);
  return key || null;
};

/**
 * Dachform-Vorbelegung je Gebäude-Typ (#689/#700).
 *
 * `data.dachform` ist auf Bestandsobjekten nicht gesetzt — ein globaler Default
 * würde jedes bereits gezeichnete Gebäude umspringen lassen. Stattdessen leitet
 * sich die Form aus dem Gebäude-Typ ab: geschlossene Wohn-/Nutzbauten bekommen
 * ein Satteldach, offene/flache Nebenbauten Pult- bzw. Flachdach. Ein ausdrücklich
 * gesetztes `dachform` übersteuert die Ableitung.
 *
 * Werte sind die Config-Optionen (satteldach/pultdach/flachdach); der 3D-Adapter
 * mappt sie auf seine kurzen roof.type-Namen.
 */
export const BUILDING_ROOF_BY_TYPE = Object.freeze({
  gartenhaus: 'satteldach',
  garage: 'flachdach',
  carport: 'pultdach',
  geraeteschuppen: 'pultdach',
  werkstatt: 'pultdach',
  poolhaus: 'satteldach',
  sauna: 'satteldach',
  pavillon: 'satteldach',
  wohnhaus: 'satteldach',
});

/**
 * Effektive Dachform eines Gebäudes: gesetzter Wert vor Typ-Ableitung.
 *
 * @param {object} [data] - obj.data (dachform, type)
 * @returns {string} Config-Dachform-Wert (satteldach/pultdach/flachdach)
 */
export const getBuildingRoofForm = (data) =>
  data?.dachform || BUILDING_ROOF_BY_TYPE[data?.type] || 'satteldach';

/**
 * Get default style for an object type
 */
export const getDefaultStyle = (typeId) => {
  const objectType = getObjectTypeById(typeId);
  return objectType?.defaultStyle || {
    fill: '#CCCCCC',
    stroke: '#999999',
    strokeWidth: 2,
    opacity: 1,
  };
};

/**
 * Get object type name by ID
 */
export const getObjectTypeName = (typeId) => {
  const objectType = getObjectTypeById(typeId);
  return objectType ? objectType.name : 'Unbekannt';
};

/**
 * Check if object type is plantable
 */
export const isObjectTypePlantable = (typeId) => {
  const objectType = getObjectTypeById(typeId);
  return objectType ? objectType.isPlantable : false;
};

/**
 * Check if object type casts shadow
 */
export const doesObjectTypeCastShadow = (typeId) => {
  const objectType = getObjectTypeById(typeId);
  return objectType ? objectType.castsShadow : false;
};

/**
 * Get category for an object type
 * @param {string} typeId - Object type ID (e.g., 'weg', 'beet')
 * @returns {string|null} Category ID or null if not found
 */
export const getCategoryForObjectType = (typeId) => {
  const objectType = getObjectTypeById(typeId);
  return objectType ? objectType.category : null;
};
